import { QueryCombinerType } from 'module/api/filter/types';
import { unreachable } from 'module/helpers/unreachable';
import { FieldFilter } from 'module/types/filter-field/filter/index';
import {
  isFieldFilterCriteria,
  isFieldFilterValid,
} from 'module/types/filter-field/grouped-filter/utils';
import { toFilterFunction } from 'module/ui/components/molecules/field-filter/filter-functions';
import { toAiFilterFunction } from 'module/ui/components/molecules/field-filter/filter-functions/ai-field';
import { EXTRA_DETAILS } from 'constant';
import type { FilterFunction } from 'module/ui/components/molecules/field-filter/types';
import type { Filter } from 'module/types/filter-field/filter/index';
import type { GroupedFilter } from 'module/types/filter-field/grouped-filter/filter';

export const getFilterFunction = (filter: Filter): FilterFunction | null =>
  filter.criteria.filter((x) => FieldFilter.isValid(x)).length === 0
    ? null
    : filter.criteria
        .filter((x) => FieldFilter.isValid(x))
        .map((criterion) => toFilterFunction(criterion))
        .reduce(getReducer(filter.type), getSeed(filter.type));

export const getGroupedFilterFunction = (
  filter: GroupedFilter,
): FilterFunction | null =>
  filter.groupCriteria.filter(({ criteria }) => isFieldFilterValid(criteria))
    .length === 0
    ? null
    : filter.groupCriteria
        .filter(({ criteria }) => isFieldFilterValid(criteria))
        .map(
          ({ id, criteria }) =>
            (record) =>
              isFieldFilterCriteria(criteria)
                ? id === filter.defaultGroup
                  ? toFilterFunction(criteria)(record)
                  : toFilterFunction(criteria)(record?.[EXTRA_DETAILS]?.[id])
                : toAiFilterFunction(criteria)(record),
        )
        .reduce(getReducer(filter.type), getSeed(filter.type));

export const getReducer =
  (type: QueryCombinerType) =>
  (previous: FilterFunction, current: FilterFunction): FilterFunction =>
  (record: Record<string, unknown>): boolean => {
    switch (type) {
      case QueryCombinerType.Any:
        return previous(record) || current(record);
      case QueryCombinerType.All:
        return previous(record) && current(record);
      default:
        return unreachable(type);
    }
  };

export const getSeed = (type: QueryCombinerType): FilterFunction => {
  switch (type) {
    case QueryCombinerType.Any:
      return () => false;
    case QueryCombinerType.All:
      return () => true;
    default:
      return unreachable(type);
  }
};

// TODO: Use translation strings
export const text = (index: number, type: QueryCombinerType) => {
  switch (index) {
    case 0:
      return 'Where';
    default:
      switch (type) {
        case QueryCombinerType.Any:
          return 'Or';
        case QueryCombinerType.All:
          return 'And';
        default:
          return unreachable(type);
      }
  }
};
